import { CLIENT_SURFACE_HEADER, readErrorResponse, setApiClient } from '@np-forge/api-client/runtime'
import { AnalyticsClientSurface } from '@np-forge/api-client/types'

import { setBypassBeforeUnload } from '@/context/LeaveModalContext'
import { ROUTES } from '@/routes/paths'
import { persistedRedirectUrl } from '@/services/redirectState.service'

const TOS_NOT_ACCEPTED_ERROR_CODE = 'TOS_NOT_ACCEPTED'

/**
 * Server-status → onboarding-redirect rules.
 *
 * The backend uses three statuses to mean "you can't proceed until you complete a
 * step": 401 (no session), 451 (current ToS not accepted), and 432 (first name not
 * valid — a reserved, non-standard code). Each sends the user to the matching
 * screen, UNLESS they are already on a screen where that status is expected: there
 * we fall through so the runtime's `customFetch` throws `FetchError` with `.status`
 * and that page settles it itself (AuthProvider.refresh, the ToS/onboarding flows) —
 * and so we never bounce a page to itself.
 *
 * 451 is shared with the region block (`UNAVAILABLE_IN_REGION`), which has no step
 * to complete, so its rule also names the ErrorResponse `error_code` it answers to.
 * Any other 451 falls through the same way, to `FetchError` with `.code`.
 *
 * `exemptPathPrefixes` are matched against `window.location.pathname`.
 */
interface StatusRedirectRule {
  status: number
  /** When set, the response body's `error_code` must match too. Rules without it never read the body. */
  errorCode?: string
  redirectTo: string
  exemptPathPrefixes: string[]
}

const STATUS_REDIRECT_RULES: StatusRedirectRule[] = [
  {
    status: 401,
    // `/invite` is exempt so the boot-time getMe() 401 does NOT fire
    // redirectAndHang() — that auto-saver persists pathname+search+hash to
    // localStorage and would leak the invite token (plan §5.1). The /invite page
    // captures the token (fragment), strips the URL, and drives its own auth flow.
    // `/work/invite` is exempt for the same reason, and because it needs no sign-in at all.
    redirectTo: ROUTES.AUTH.LOGIN.fullPath,
    exemptPathPrefixes: [
      ROUTES.AUTH.ROOT.fullPath,
      ROUTES.TERMS_OF_SERVICE.fullPath,
      ROUTES.CUSTOMER_SUPPORT.fullPath,
      ROUTES.B2B.TERMS_OF_SERVICE.fullPath,
      ROUTES.B2B.CUSTOMER_SUPPORT.fullPath,
      ROUTES.B2B.INVITE.fullPath,
      ROUTES.INVITE.fullPath,
    ],
  },
  {
    status: 451,
    errorCode: TOS_NOT_ACCEPTED_ERROR_CODE,
    redirectTo: ROUTES.ACCEPT_TERMS_OF_SERVICE.fullPath,
    exemptPathPrefixes: [ROUTES.ACCEPT_TERMS_OF_SERVICE.fullPath],
  },
  {
    status: 432,
    redirectTo: ROUTES.ONBOARDING.fullPath,
    exemptPathPrefixes: [ROUTES.ONBOARDING.fullPath],
  },
]

function isOnAnyPathPrefix(prefixes: string[]): boolean {
  return prefixes.some(prefix => window.location.pathname.startsWith(prefix))
}

/** Reads a clone, so `customFetch` can still parse the body; a non-JSON body never matches. */
async function responseMatchesRuleErrorCode(rule: StatusRedirectRule, response: Response): Promise<boolean> {
  if (typeof rule.errorCode === 'undefined') return true
  const { code } = await readErrorResponse(response.clone())
  return code === rule.errorCode
}

/**
 * Persist the current location, suppress the leave-modal, navigate to `redirectTo`,
 * and return a never-resolving promise so the in-flight request hangs while the
 * browser navigates away (no error flashes through the originating hook).
 */
function redirectAndHang(redirectTo: string): Promise<Response> {
  persistedRedirectUrl.save(window.location.pathname + window.location.search + window.location.hash)
  setBypassBeforeUnload(true)
  window.location.replace(redirectTo)
  return new Promise<Response>(() => {})
}

/**
 * Wires the shared API client mutator to this app's auth + nav semantics.
 *
 * Must be called once before any React Query hook fires. The shared `customFetch`
 * (in @np-forge/api-client/runtime) prepends baseUrl, parses JSON, and throws
 * `FetchError` (with `.status`) on non-OK responses. The status redirect-and-hang
 * policy lives here because it's a frontend UX concern, not a runtime contract —
 * other consumers (MCP, mobile) wire their own response-status policy in their own
 * `fetch` wrapper.
 */
export function configureApiClient(): void {
  setApiClient({
    baseUrl: import.meta.env.VITE_API_BASE || '',
    // Attributes server-emitted product-analytics events to the web app. Adds a CORS preflight to
    // otherwise-simple GETs (a custom header is never a CORS-safelisted one); the backend allows the
    // header and caches preflights for 24h, so it costs one OPTIONS per path per day.
    defaultHeaders: { [CLIENT_SURFACE_HEADER]: AnalyticsClientSurface.web_app },
    fetch: async (url, opts) => {
      const response = await fetch(url, { ...opts, credentials: 'include' })

      const rule = STATUS_REDIRECT_RULES.find(candidate => candidate.status === response.status)
      if (rule && !isOnAnyPathPrefix(rule.exemptPathPrefixes) && (await responseMatchesRuleErrorCode(rule, response))) {
        return redirectAndHang(rule.redirectTo)
      }

      return response
    },
  })
}
